import { useState, useRef, useEffect } from 'react';
import {
  MousePointer2,
  Type,
  Square,
  Image,
  Minus,
  PenTool,
  Pipette,
  Hand,
  MousePointerClick,
  ChevronDown,
  FileUp,
} from 'lucide-react';
import { useCanvasStore, type EditorTool } from '../../stores/canvasStore';
import ShapePicker from './ShapePicker';

interface ToolConfig {
  id: EditorTool;
  icon: React.ElementType;
  label: string;
  shortcut: string;
}

const TOOLS: ToolConfig[] = [
  { id: 'select', icon: MousePointer2, label: 'Select', shortcut: 'V' },
  { id: 'text', icon: Type, label: 'Text', shortcut: 'T' },
  { id: 'shape', icon: Square, label: 'Shape', shortcut: 'R' },
  { id: 'image', icon: Image, label: 'Image', shortcut: 'I' },
  { id: 'line', icon: Minus, label: 'Line', shortcut: 'L' },
  { id: 'pen', icon: PenTool, label: 'Pen', shortcut: 'P' },
  { id: 'button', icon: MousePointerClick, label: 'Button', shortcut: 'B' },
  { id: 'eyedropper', icon: Pipette, label: 'Eyedropper', shortcut: '' },
  { id: 'pan', icon: Hand, label: 'Pan', shortcut: 'H' },
];

export default function ToolStrip() {
  const { activeTool, setActiveTool, setPendingShapeType, lastSampledColor } = useCanvasStore();
  const [showShapePicker, setShowShapePicker] = useState(false);
  const shapePickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (shapePickerRef.current && !shapePickerRef.current.contains(e.target as Node)) {
        setShowShapePicker(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleToolClick = (toolId: EditorTool) => {
    if (toolId === 'shape') {
      setShowShapePicker(!showShapePicker);
      setActiveTool('shape');
    } else {
      setShowShapePicker(false);
      setActiveTool(toolId);
    }
  };

  const handleShapeSelect = (shapeType: string) => {
    setPendingShapeType(shapeType);
    setActiveTool('shape');
    setShowShapePicker(false);
  };

  return (
    <div className="w-11 bg-[#141414] border-r border-white/[0.06] flex flex-col items-center py-2 shrink-0">
      {TOOLS.map((tool) => {
        const Icon = tool.icon;
        const isActive = activeTool === tool.id;
        const isShape = tool.id === 'shape';
        const isPan = tool.id === 'pan';

        return (
          <div
            key={tool.id}
            className={`relative w-full flex justify-center ${isPan ? 'mt-auto' : ''}`}
            ref={isShape ? shapePickerRef : undefined}
          >
            {isPan && (
              <div className="absolute top-0 left-2 right-2 border-t border-white/[0.06]" />
            )}
            <button
              onClick={() => handleToolClick(tool.id)}
              className={`
                relative w-9 h-9 flex items-center justify-center rounded-lg
                transition-all duration-150 group
                ${isPan ? 'mt-2' : ''}
                ${isActive
                  ? 'bg-cyan-500/15 text-cyan-400'
                  : 'text-zinc-500 hover:text-zinc-300 hover:bg-white/[0.06]'
                }
              `}
              title={`${tool.label}${tool.shortcut ? ` (${tool.shortcut})` : ''}`}
            >
              <Icon size={16} />
              {isShape && (
                <ChevronDown size={8} className="absolute bottom-1 right-1 opacity-40" />
              )}

              {isActive && (
                <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[2px] h-5 bg-cyan-400 rounded-r" />
              )}

              <div className="
                absolute left-full ml-3 px-2.5 py-1.5
                bg-[#1a1a1a] border border-white/[0.08] text-zinc-200 text-[11px] rounded-md
                opacity-0 group-hover:opacity-100
                pointer-events-none whitespace-nowrap
                transition-opacity duration-150 z-50
                shadow-lg shadow-black/30
              ">
                {tool.label}
                {tool.shortcut && (
                  <span className="ml-2 text-zinc-500 text-[10px]">{tool.shortcut}</span>
                )}
              </div>
            </button>

            {isShape && showShapePicker && (
              <div className="absolute left-full top-0 ml-2 z-50">
                <ShapePicker
                  isOpen={showShapePicker}
                  onClose={() => setShowShapePicker(false)}
                  onSelectShape={handleShapeSelect}
                />
              </div>
            )}
          </div>
        );
      })}

      <div className="w-6 h-px bg-zinc-700/60 my-1" />
      <div className="relative w-full flex justify-center">
        <button
          onClick={() => document.dispatchEvent(new CustomEvent('cs:import-svg'))}
          className="w-9 h-9 flex items-center justify-center rounded-lg text-zinc-500 hover:text-zinc-300 hover:bg-white/[0.06] transition-all duration-150 group"
          title="Import SVG (Ctrl+Shift+I)"
        >
          <FileUp size={16} />
          <div className="
            absolute left-full ml-3 px-2.5 py-1.5
            bg-[#1a1a1a] border border-white/[0.08] text-zinc-200 text-[11px] rounded-md
            opacity-0 group-hover:opacity-100
            pointer-events-none whitespace-nowrap
            transition-opacity duration-150 z-50
            shadow-lg shadow-black/30
          ">
            Import SVG
            <span className="ml-2 text-zinc-500 text-[10px]">Ctrl+Shift+I</span>
          </div>
        </button>
      </div>

      {lastSampledColor && activeTool === 'select' && (
        <div className="mt-2 px-2 w-full flex justify-center">
          <div
            className="w-6 h-6 rounded-md border border-white/20 cursor-pointer hover:border-white/40 transition-colors"
            style={{ backgroundColor: lastSampledColor }}
            title={`Last sampled: ${lastSampledColor}`}
            onClick={() => navigator.clipboard.writeText(lastSampledColor)}
          />
        </div>
      )}
    </div>
  );
}
